import { createElement, useState } from "react";
import { AnnualWheelActivityTemplateListItem, AnnualWheelActivityTemplateTableModel } from "./AnnualWheelActivityTemplatesList.types";
import { useMasterDataBreadCrumbProvider } from "../useMasterDataBreadCrumbProvider.ts";
import { useTranslation } from "../../localization/useTranslation"; // adjust path as needed
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { deleteHttp } from "../../common/api/apiShared.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useRecurringIntervalFormatting } from "../../annualWheel/AnnualWheel.hooks.ts";
import { IAnnualWheelActivityTemplatesListProps } from "./AnnualWheelActivityTemplatesList.tsx";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider.ts";

export function useAnnualWheelActivityTemplatesList(props: IAnnualWheelActivityTemplatesListProps) {
    const url = `/AnnualWheelActivityTemplate/list?displayObligations=${props.isObligation}`;
    const { isLoading, data: activityTemplates, refetch } = useDotLegalQuery<Array<AnnualWheelActivityTemplateListItem>>(url);
    const masterDataBreadCrumbProvider = useMasterDataBreadCrumbProvider();
    const { permissions } = useUserContext();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [idForUpdate, setIdForUpdate] = useState<string>();
    const { translateString } = useTranslation();
    const [templateForDeletion, setTemplateForDeletion] = useState<AnnualWheelActivityTemplateListItem | undefined>(undefined);
    const snackBar = useDotLegalSnackbar();
    const formatRecurringInterval = useRecurringIntervalFormatting();
    const { hasTagsAccess } = usePermissionsProvider();

    const breadCrumbString = props.isObligation ? translateString("obligationTemplates") : translateString("annualWheelActivityTemplates");
    const breadCrumbs = masterDataBreadCrumbProvider.getBreadCrumbs(breadCrumbString);

    const deleteTemplate = async (id: string) => {
        return await deleteHttp(`/AnnualWheelActivityTemplate/${id}`);
    };

    const deleteMutation = useDotLegalMutation(deleteTemplate, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: templateForDeletion?.name } }));
                setTemplateForDeletion(undefined);
            } else {
                snackBar.show(createElement(Trans, { i18nKey: "cantDeleteTemplate", values: { name: templateForDeletion?.name } }), "warning");
            }
        },
    });

    const onCreateClick = () => {
        setCreateDialogOpen(true);
    };

    const onUpdateClick = (id: string) => {
        setIdForUpdate(id);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDialogClose = () => {
        setIdForUpdate(undefined);
        setCreateDialogOpen(false);
    };

    const getMenuItems = (template: AnnualWheelActivityTemplateListItem) => {
        let menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setIdForUpdate(template.id);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setTemplateForDeletion(template);
                },
            });
        }

        return menuItems;
    };

    const onDelete = () => {
        deleteMutation.mutate(templateForDeletion!.id);
    };

    const activityTemplatesTableModel: Array<AnnualWheelActivityTemplateTableModel> =
        activityTemplates?.map((t) => ({
            ...t,
            complianceAreaString: t.complianceAreas.map((x) => x.name).join(", "),
            recurringIntervalString: formatRecurringInterval(t.recurringInterval, t.recurringIntervalType),
            businessAreaString: t.businessAreas.map((x) => x.name).join(", "),
            tagsString: t.tags.map((x) => x.name).join(", "),
        })) ?? [];

    return {
        isLoading,
        hasTagsAccess,
        activityTemplatesTableModel,
        breadCrumbs,
        canManageMasterData: permissions.canManageMasterData,
        onCreateClick,
        createDialogOpen,
        onUpdateClick,
        onDialogClose,
        onSaved,
        templateToUpdate: activityTemplates?.find((t) => t.id === idForUpdate),
        getMenuItems,
        templateForDeletion,
        setTemplateForDeletion,
        onDelete,
        isDeleting: deleteMutation.isLoading,
    };
}
